import { Box, ButtonBase, IconButton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { ChangelogDay } from "@xop/dashboard-shared";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";

import { TYPE_META } from "./meta.js";

/**
 * Read-only month-grid calendar for the Change Log.
 *
 * Purpose-built (not a date-picker): this one paints per-day change-type
 * dots + a count and is a pure click target, with stable selectors
 * (`data-date`, descriptive `aria-label`) for unit + e2e tests.
 * Monday-first. `today` is injectable so tests can pin "today"
 * deterministically.
 *
 * Self-contained MUI: a CSS-grid `Box` (7 columns — weekday headers +
 * day cells as `ButtonBase`). No react-day-picker / Tailwind; the
 * highlight/selected/today states resolve through the MUI palette so it
 * re-skins with `theme.ts`.
 */
export interface ChangelogCalendarProps {
  /** `YYYY-MM`. */
  month: string;
  days: ChangelogDay[];
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onToday: () => void;
  /** Defaults to the real current date; pinned in tests. */
  today?: Date;
  /** Denser cells for the narrow sidebar (the event-first layout). */
  compact?: boolean;
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Local `YYYY-MM-DD` for a Date (no UTC shift). */
function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

interface Cell {
  date: string;
  dayOfMonth: number;
  inMonth: boolean;
}

function buildCells(month: string): Cell[] {
  const [y, m] = month.split("-").map(Number) as [number, number];
  const monthIndex = m - 1;
  const first = new Date(y, monthIndex, 1);
  // Monday-first lead count (JS getDay: 0=Sun).
  const lead = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(y, monthIndex + 1, 0).getDate();
  const weeks = Math.ceil((lead + daysInMonth) / 7);
  const cells: Cell[] = [];
  for (let i = 0; i < weeks * 7; i += 1) {
    // `new Date` normalizes negative / overflow day-of-month into the
    // adjacent months, so leading/trailing cells render the right dates.
    const d = new Date(y, monthIndex, i - lead + 1);
    cells.push({
      date: ymd(d),
      dayOfMonth: d.getDate(),
      inMonth: d.getMonth() === monthIndex,
    });
  }
  return cells;
}

function monthLabel(month: string): string {
  const [y, m] = month.split("-").map(Number) as [number, number];
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
}

export function ChangelogCalendar({
  month,
  days,
  selectedDate,
  onSelectDate,
  onPrevMonth,
  onNextMonth,
  onToday,
  today,
  compact = false,
}: ChangelogCalendarProps): JSX.Element {
  const dayMap = new Map(days.map((d) => [d.date, d]));
  const todayStr = ymd(today ?? new Date());
  const cells = buildCells(month);
  const cellMinH = compact ? 44 : 64;
  const dotSize = compact ? 6 : 8;

  return (
    // `minWidth: 0` so the grid never out-grows its (often narrow, fixed-width)
    // container — the parent Card has `overflow: hidden`, so any overflow here
    // would clip the month label + the right-hand columns.
    <Box sx={{ display: "grid", gap: 1.5, minWidth: 0 }}>
      {/* Month nav */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <IconButton
            size="small"
            aria-label="Previous month"
            onClick={onPrevMonth}
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              color: "text.secondary",
            }}
          >
            <ChevronLeftIcon size={16} />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Next month"
            onClick={onNextMonth}
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              color: "text.secondary",
            }}
          >
            <ChevronRightIcon size={16} />
          </IconButton>
          <ButtonBase
            onClick={onToday}
            sx={{
              borderRadius: 1,
              px: 1,
              py: 0.5,
              fontSize: 13,
              fontWeight: 500,
              color: "text.secondary",
              "&:hover": { bgcolor: "surfaceSoft" },
            }}
          >
            Today
          </ButtonBase>
        </Box>
        <Typography
          component="h2"
          sx={{ fontSize: 14, fontWeight: 600, color: "text.primary" }}
        >
          {monthLabel(month)}
        </Typography>
      </Box>

      {/* Weekday header */}
      <Box
        sx={{
          display: "grid",
          // `minmax(0, 1fr)` (not bare `1fr`, which floors at min-content) so
          // the seven columns can shrink to fit a narrow container instead of
          // overflowing it and being clipped by the Card.
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          gap: 0.5,
          textAlign: "center",
          fontSize: 12,
          fontWeight: 500,
          color: "text.disabled",
        }}
      >
        {WEEKDAYS.map((w) => (
          <Box key={w} sx={{ py: 0.5 }}>
            {w}
          </Box>
        ))}
      </Box>

      {/* Day grid */}
      <Box
        role="grid"
        sx={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 0.5 }}
      >
        {cells.map((cell, i) => {
          if (!cell.inMonth) {
            return (
              <Box
                key={`${cell.date}-${i}`}
                aria-hidden="true"
                sx={{
                  minHeight: cellMinH,
                  borderRadius: 1,
                  border: 1,
                  borderColor: "transparent",
                  p: 0.5,
                  textAlign: "right",
                  fontSize: 12,
                  color: "text.disabled",
                  opacity: 0.4,
                }}
              >
                {cell.dayOfMonth}
              </Box>
            );
          }

          const day = dayMap.get(cell.date);
          const isToday = cell.date === todayStr;
          const isSelected = cell.date === selectedDate;
          const count = day?.count ?? 0;
          const label =
            count > 0
              ? `${cell.date}, ${count} update${count === 1 ? "" : "s"}`
              : `${cell.date}, no updates`;

          return (
            <ButtonBase
              key={cell.date}
              role="gridcell"
              data-date={cell.date}
              data-has-updates={count > 0 ? "true" : "false"}
              aria-label={label}
              aria-pressed={isSelected}
              onClick={() => onSelectDate(cell.date)}
              sx={{
                minHeight: cellMinH,
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                justifyContent: "flex-start",
                borderRadius: 1,
                border: 1,
                p: 0.5,
                textAlign: "left",
                transition: (t) => t.transitions.create(["background-color", "border-color"], { duration: 150 }),
                ...(isSelected
                  ? {
                      borderColor: "primary.main",
                      bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                    }
                  : {
                      borderColor: "divider",
                      "&:hover": { bgcolor: "surfaceSoft" },
                    }),
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    height: 20,
                    minWidth: 20,
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 999,
                    px: 0.5,
                    fontSize: 12,
                    ...(isToday
                      ? { bgcolor: "primary.main", fontWeight: 600, color: "primary.contrastText" }
                      : { color: "text.primary" }),
                  }}
                >
                  {cell.dayOfMonth}
                </Box>
                {count > 0 && (
                  <Box component="span" sx={{ fontSize: 10, fontWeight: 500, color: "text.disabled" }}>
                    {count}
                  </Box>
                )}
              </Box>
              {day && day.types.length > 0 && (
                <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", gap: 0.25 }}>
                  {day.types.map((t) => (
                    <Box
                      key={t}
                      component="span"
                      title={TYPE_META[t].label}
                      sx={{
                        width: dotSize,
                        height: dotSize,
                        borderRadius: "50%",
                        bgcolor: TYPE_META[t].dot,
                      }}
                    />
                  ))}
                </Box>
              )}
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
}
